import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { kuniriIconWeight, KuniriIcons } from '@/lib/icons'

import type { LocationSelection } from '../hooks/use-location-selection'
import type {
  CitySuggestion,
  PlaceSuggestion,
} from '../types'

interface LocationFieldsProps {
  location: LocationSelection
  cityError?: string
  placeError?: string
  disabled?: boolean
}

function CityResults({
  items,
  onSelect,
}: {
  items: CitySuggestion[]
  onSelect: (suggestion: CitySuggestion) => void
}) {
  return (
    <ul className="activity-search-results">
      {items.map((suggestion) => (
        <li
          key={`${suggestion.city}-${suggestion.latitude}-${suggestion.longitude}`}
        >
          <button
            type="button"
            className="activity-search-result"
            onClick={() => onSelect(suggestion)}
          >
            <KuniriIcons.buildings
              size={18}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            <span>
              <span className="block font-semibold">{suggestion.label}</span>
              {suggestion.label !== suggestion.city ? (
                <span className="block text-xs text-muted">
                  {suggestion.city}
                </span>
              ) : null}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

function PlaceResults({
  items,
  onSelect,
}: {
  items: PlaceSuggestion[]
  onSelect: (suggestion: PlaceSuggestion) => void
}) {
  return (
    <ul className="activity-search-results">
      {items.map((suggestion) => (
        <li
          key={`${suggestion.label}-${suggestion.latitude}-${suggestion.longitude}`}
        >
          <button
            type="button"
            className="activity-search-result"
            onClick={() => onSelect(suggestion)}
          >
            <KuniriIcons.mapPin
              size={18}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            <span className="font-semibold">{suggestion.label}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * City and meeting-place fields. All visible copy comes from the catalogs;
 * suggestion labels are provider data and are rendered as supplied.
 */
export function LocationFields({
  location,
  cityError,
  placeError,
  disabled = false,
}: LocationFieldsProps) {
  const { t } = useTranslation()
  const cityId = useId()
  const placeId = useId()
  const cityErrorId = `${cityId}-error`
  const cityHintId = `${cityId}-hint`
  const placeErrorId = `${placeId}-error`
  const placeHintId = `${placeId}-hint`
  const placeCityName = location.selectedCity?.city
  const cityDescribedBy = [cityError ? cityErrorId : null, cityHintId]
    .filter(Boolean)
    .join(' ')
  const placeDescribedBy = [placeError ? placeErrorId : null, placeHintId]
    .filter(Boolean)
    .join(' ')

  const showManualCityFallback =
    location.cityPhase === 'error' ||
    (location.cityAttempted && location.cityItems.length === 0)
  const showManualCityButton =
    !location.manualCity &&
    !location.hasSelectedCity &&
    (showManualCityFallback || location.cityValue.trim().length >= 2)
  const showManualPlaceButton =
    !location.manualPlace &&
    !location.hasSelectedPlace &&
    (!location.canSearchPlaces ||
      location.placePhase === 'error' ||
      (location.placeAttempted && location.placeItems.length === 0) ||
      location.placeValue.trim().length >= 2)

  return (
    <div className="space-y-4">
      <div>
        <label className="field-label" htmlFor={cityId}>
          {t('activityForm.fields.city')}
        </label>
        <div className="relative">
          <KuniriIcons.magnifyingGlass
            size={19}
            weight={kuniriIconWeight}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted"
          />
          <input
            id={cityId}
            type="text"
            value={location.cityValue}
            disabled={disabled}
            maxLength={120}
            required
            autoComplete="off"
            placeholder={t('activityForm.location.cityPlaceholder')}
            aria-invalid={cityError ? true : undefined}
            aria-describedby={cityDescribedBy}
            className={`field-input pr-11 pl-10 ${
              cityError ? 'border-danger' : ''
            }`}
            onChange={(event) => location.setCity(event.target.value)}
          />
          {location.hasSelectedCity ? (
            <KuniriIcons.checkCircle
              size={21}
              weight={kuniriIconWeight}
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-teal"
            />
          ) : null}
        </div>
        <p
          id={cityHintId}
          className="mt-1.5 text-xs leading-snug text-muted"
        >
          {t('activityForm.location.cityHint')}
        </p>
        {cityError ? (
          <p
            id={cityErrorId}
            className="mt-1.5 text-sm font-semibold text-danger"
          >
            {cityError}
          </p>
        ) : null}

        {location.cityPhase === 'loading' ? (
          <p role="status" className="mt-2 text-sm text-muted">
            {t('activityForm.location.searching')}
          </p>
        ) : null}

        {location.cityPhase === 'error' ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted">
            <span>
              {t(
                location.cityErrorKey ?? 'activityForm.location.searchError',
              )}
            </span>
            <button
              type="button"
              className="btn-ghost min-h-11 px-2"
              onClick={location.retryCity}
            >
              {t('common.retry')}
            </button>
          </div>
        ) : null}

        {location.cityAttempted && location.cityItems.length === 0 ? (
          <p className="mt-2 rounded-md border border-line bg-paper px-3 py-2 text-sm text-muted">
            {t('activityForm.location.cityEmpty')}
          </p>
        ) : null}

        {location.cityItems.length > 0 ? (
          <CityResults items={location.cityItems} onSelect={location.chooseCity} />
        ) : null}

        {showManualCityButton ? (
          <button
            type="button"
            className="btn-ghost mt-1 min-h-11 px-2 text-sm"
            onClick={location.useManualCity}
          >
            {t('activityForm.location.manualCity')}
          </button>
        ) : null}

        {location.manualCity ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-xs text-muted">
              {t('activityForm.location.manualCityHint')}
            </p>
            <button
              type="button"
              className="btn-ghost min-h-11 px-2 text-sm"
              onClick={location.searchCityAgain}
            >
              {t('activityForm.location.searchAgain')}
            </button>
          </div>
        ) : null}
      </div>

      <div>
        <label className="field-label" htmlFor={placeId}>
          {t('activityForm.fields.place')}
        </label>
        <div className="relative">
          <KuniriIcons.mapPin
            size={19}
            weight={kuniriIconWeight}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted"
          />
          <input
            id={placeId}
            type="text"
            value={location.placeValue}
            disabled={
              disabled || (!location.selectedCity && !location.manualCity)
            }
            maxLength={160}
            autoComplete="off"
            placeholder={t('activityForm.location.placePlaceholder')}
            aria-invalid={placeError ? true : undefined}
            aria-describedby={placeDescribedBy}
            className={`field-input pr-11 pl-10 ${
              placeError ? 'border-danger' : ''
            }`}
            onChange={(event) => location.setPlace(event.target.value)}
          />
          {location.hasSelectedPlace ? (
            <KuniriIcons.checkCircle
              size={21}
              weight={kuniriIconWeight}
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-teal"
            />
          ) : null}
        </div>
        <p
          id={placeHintId}
          className="mt-1.5 text-xs leading-snug text-muted"
        >
          {t('activityForm.location.placeHint')}
        </p>
        {placeError ? (
          <p
            id={placeErrorId}
            className="mt-1.5 text-sm font-semibold text-danger"
          >
            {placeError}
          </p>
        ) : null}

        {!location.canSearchPlaces && location.manualCity ? (
          <p className="mt-2 text-xs leading-snug text-muted">
            {t('activityForm.location.manualCityPlaceHint')}
          </p>
        ) : null}

        {location.selectedCity && !location.canSearchPlaces ? (
          <p className="mt-2 text-xs leading-snug text-muted">
            {t('activityForm.location.coordinatesUnavailable')}
          </p>
        ) : null}

        {location.placePhase === 'loading' ? (
          <p role="status" className="mt-2 text-sm text-muted">
            {t('activityForm.location.searching')}
          </p>
        ) : null}

        {location.placePhase === 'error' ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted">
            <span>
              {t(
                location.placeErrorKey ?? 'activityForm.location.searchError',
              )}
            </span>
            <button
              type="button"
              className="btn-ghost min-h-11 px-2"
              onClick={location.retryPlace}
            >
              {t('common.retry')}
            </button>
          </div>
        ) : null}

        {location.placeAttempted && location.placeItems.length === 0 ? (
          <p className="mt-2 rounded-md border border-line bg-paper px-3 py-2 text-sm text-muted">
            {placeCityName
              ? t('activityForm.location.placeEmpty', { city: placeCityName })
              : t('activityForm.location.placeEmptyGeneric')}
          </p>
        ) : null}

        {location.placeItems.length > 0 ? (
          <PlaceResults
            items={location.placeItems}
            onSelect={location.choosePlace}
          />
        ) : null}

        {showManualPlaceButton ? (
          <button
            type="button"
            className="btn-ghost mt-1 min-h-11 px-2 text-sm"
            onClick={location.useManualPlace}
          >
            {t('activityForm.location.manualPlace')}
          </button>
        ) : null}

        {location.manualPlace ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-xs text-muted">
              {t('activityForm.location.manualPlaceHint')}
            </p>
            {location.canSearchPlaces ? (
              <button
                type="button"
                className="btn-ghost min-h-11 px-2 text-sm"
                onClick={location.searchPlaceAgain}
              >
                {t('activityForm.location.searchAgain')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <p className="text-[11px] text-muted">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          {t('activityForm.location.attribution')}
        </a>
      </p>
    </div>
  )
}
